import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute, { PAYOUT_ROLES } from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Chat from "@/pages/Chat";
import Admin from "@/pages/Admin";
import LPGReport from "@/pages/LPGReport";
import BulkPaste from "@/pages/BulkPaste";
import Complaints from "@/pages/Complaints";
import Home from "@/pages/Home";
import ResendOnly from "@/pages/ResendOnly";
import WalletPage from "@/pages/Wallet";
import MyLedger from "@/pages/MyLedger";
import AgencyLedger from "@/pages/AgencyLedger";
import BankDetails from "@/pages/BankDetails";
import DeliveryLogins from "@/pages/DeliveryLogins";
import CoolingTracker from "@/pages/CoolingTracker";
import MyCooling from "@/pages/MyCooling";
import EkycDesk from "@/pages/EkycDesk";
import PayoutManager from "@/pages/PayoutManager";
import MyPayout from "@/pages/MyPayout";
import PendingDeliveries from "@/pages/PendingDeliveries";
import DeliveryApp from "@/pages/DeliveryApp";
import SmsInbox from "@/pages/SmsInbox";
import AgencyEkyc from "@/pages/AgencyEkyc";
import EkycBulkPaste from "@/pages/EkycBulkPaste";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "resend_staff") return <Navigate to="/resend-only" replace />;
  if (user.role === "team_staff") return <Navigate to="/my-ledger" replace />;
  if (user.role === "ekyc_staff") return <Navigate to="/ekyc-desk" replace />;
  if (user.role === "delivery_man") return <Navigate to="/delivery" replace />;
  if (PAYOUT_ROLES.includes(user.role)) return <Navigate to="/my-payout" replace />;
  return <Navigate to="/home" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bank-details"
            element={
              <ProtectedRoute>
                <BankDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lpg-report"
            element={
              <ProtectedRoute>
                <LPGReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulk-paste"
            element={
              <ProtectedRoute>
                <BulkPaste />
              </ProtectedRoute>
            }
          />
          <Route
            path="/complaints"
            element={
              <ProtectedRoute>
                <Complaints />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resend-only"
            element={
              <ProtectedRoute allowResendStaff allowTeamStaff>
                <ResendOnly />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/wallet"
            element={
              <ProtectedRoute requireAdmin>
                <WalletPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payouts"
            element={
              <ProtectedRoute requireAdmin>
                <PayoutManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-payout"
            element={
              <ProtectedRoute allowPayout>
                <MyPayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-ledger"
            element={
              <ProtectedRoute allowTeamStaff>
                <MyLedger />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency-ledger"
            element={
              <ProtectedRoute>
                <AgencyLedger />
              </ProtectedRoute>
            }
          />
          <Route
            path="/delivery-logins"
            element={
              <ProtectedRoute requireAdmin>
                <DeliveryLogins />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ekyc"
            element={
              <ProtectedRoute>
                <AgencyEkyc />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ekyc/paste"
            element={
              <ProtectedRoute requireAdmin>
                <EkycBulkPaste />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sms-inbox"
            element={
              <ProtectedRoute requireAdmin>
                <SmsInbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cooling-tracker"
            element={
              <ProtectedRoute requireAdmin>
                <CoolingTracker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-cooling"
            element={
              <ProtectedRoute>
                <MyCooling />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ekyc-desk"
            element={
              <ProtectedRoute allowEkycStaff allowTeamStaff allowResendStaff allowDeliveryMan>
                <EkycDesk />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pending-deliveries"
            element={
              <ProtectedRoute allowDeliveryMan>
                <PendingDeliveries />
              </ProtectedRoute>
            }
          />
          <Route
            path="/delivery/*"
            element={
              <ProtectedRoute allowDeliveryMan>
                <DeliveryApp />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="top-right" closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
